import { ReactNode } from "react";
import {
  Button,
  InputAdornment,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { commonStyles } from "../commonStyles";

// Shared manage-page filter controls (platform UI/UX polish, Phase A). The
// popup Menus stay page-owned — these are the row layout, search field and
// trigger/clear buttons only. Every default is a verbatim copy of what
// ManageMembers renders today; the other manage pages adopt in Phase B by
// overriding the escape-hatch props.

const cpaGold = commonStyles.cpaGold;
const cpaGoldDark = commonStyles.cpaGoldDark;

export const filterControlBorderColor = "#8F969E";

// Verbatim ManageMembers search-field sx.
export const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "6px",
    height: 56,
    "& fieldset": {
      borderColor: filterControlBorderColor,
    },
    "&:hover fieldset": {
      borderColor: "#6B7280",
    },
    "&.Mui-focused fieldset": {
      borderColor: cpaGold,
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#8F969E",
    opacity: 1,
  },
};

// Verbatim ManageMembers filter-menu button sx.
export const filterButtonSx = {
  backgroundColor: "white",
  borderColor: filterControlBorderColor,
  borderRadius: "6px",
  color: "#6B7280",
  fontSize: 15,
  fontWeight: 700,
  height: 56,
  justifyContent: "flex-start",
  minWidth: 0,
  px: 2,
  textTransform: "none",
  whiteSpace: "nowrap",
  width: "100%",
  "& .MuiButton-startIcon": {
    color: "#8F969E",
    mr: 1.25,
  },
  "&:hover": {
    backgroundColor: "white",
    borderColor: "#6B7280",
  },
};

// Gold-bordered variant signalling the filter differs from the default
// selection ("indicates active filter").
export const activeFilterButtonSx = {
  ...filterButtonSx,
  borderColor: cpaGold,
  color: "#111827",
  "& .MuiButton-startIcon": {
    color: cpaGold,
    mr: 1.25,
  },
  "&:hover": {
    backgroundColor: "white",
    borderColor: cpaGoldDark,
  },
};

// Verbatim ManageMembers red "Clear All Filters" sx.
export const clearFiltersButtonSx = {
  backgroundColor: "#F31723",
  borderRadius: "6px",
  color: "white",
  fontSize: 15,
  fontWeight: 800,
  height: 56,
  textTransform: "none",
  width: "100%",
  "&:hover": { backgroundColor: "#D90F1A" },
};

// Verbatim ManageMembers gold count pill.
export const filterCountBadgeClassName =
  "ml-2 inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-cpaGold px-1.5 text-xs font-extrabold text-white";

export type FilterBarProps = {
  children: ReactNode;
  className?: string;
};

export const FilterBar = ({
  children,
  className = "mt-5 flex flex-col gap-3 xl:flex-row xl:items-center",
}: FilterBarProps) => <div className={className}>{children}</div>;

export type FilterSearchFieldProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  // applied to the TextField itself
  className?: string;
  fullWidth?: boolean;
  adornmentIcon?: ReactNode;
  fieldSx?: SxProps<Theme>;
};

export const FilterSearchField = ({
  value,
  onChange,
  placeholder,
  className = "min-w-[280px] xl:flex-[1_1_560px]",
  fullWidth = true,
  adornmentIcon,
  fieldSx = filterFieldSx,
}: FilterSearchFieldProps) => (
  <TextField
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    fullWidth={fullWidth}
    className={className}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          {adornmentIcon ?? <SearchIcon sx={{ color: "#8F969E" }} />}
        </InputAdornment>
      ),
    }}
    sx={fieldSx}
  />
);

export type FilterMenuButtonProps = {
  // static ("Filter by Member Status") or dynamic (Invoices date-range label)
  label: ReactNode;
  // page stores the anchor + renders its own <Menu>
  onOpen: (anchorEl: HTMLElement) => void;
  // true → activeFilterButtonSx (gold border), else filterButtonSx
  active?: boolean;
  // Events/Invoices date buttons pass <CalendarTodayOutlinedIcon />
  startIcon?: ReactNode;
  // non-null → gold count pill; Members passes
  // statusFilterIsActive ? selectedStatuses.length : null
  badgeCount?: number | null;
  badgeAriaLabel?: string;
  // Members wraps in <div className="xl:w-[260px]">; undefined → no wrapper
  wrapperClassName?: string;
  // merged over the chosen base sx (Events Phase B removes width:"100%")
  sx?: SxProps<Theme>;
};

export const FilterMenuButton = ({
  label,
  onOpen,
  active = false,
  startIcon,
  badgeCount = null,
  badgeAriaLabel,
  wrapperClassName,
  sx,
}: FilterMenuButtonProps) => {
  const baseSx = active ? activeFilterButtonSx : filterButtonSx;
  const button = (
    <Button
      type="button"
      variant="outlined"
      startIcon={startIcon ?? <TuneOutlinedIcon />}
      onClick={(event) => onOpen(event.currentTarget)}
      sx={
        sx
          ? ([baseSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>)
          : baseSx
      }
    >
      {label}
      {badgeCount !== null && badgeCount !== undefined && (
        <span aria-label={badgeAriaLabel} className={filterCountBadgeClassName}>
          {badgeCount}
        </span>
      )}
    </Button>
  );

  return wrapperClassName ? (
    <div className={wrapperClassName}>{button}</div>
  ) : (
    button
  );
};

export type ClearFiltersButtonProps = {
  onClick: () => void;
  // Members: !hasActiveFilters; Events/Emails never pass (always enabled)
  disabled?: boolean;
  // POL-013 / Q-026 (answered: plural platform-wide): the label is
  // "Clear All Filters" everywhere; the old Events/Emails singular
  // "Clear All Filter" was normalized in the B6 polish pass.
  label?: string;
  // Members: "xl:w-[220px]"
  wrapperClassName?: string;
  sx?: SxProps<Theme>;
};

export const ClearFiltersButton = ({
  onClick,
  disabled,
  label = "Clear All Filters",
  wrapperClassName,
  sx = clearFiltersButtonSx,
}: ClearFiltersButtonProps) => {
  const button = (
    <Button
      type="button"
      variant="contained"
      onClick={onClick}
      disabled={disabled}
      sx={sx}
    >
      {label}
    </Button>
  );

  return wrapperClassName ? (
    <div className={wrapperClassName}>{button}</div>
  ) : (
    button
  );
};
